import { useMemo } from 'react';
import { startOfDay, endOfDay, subDays, format } from 'date-fns';
import { useLocationNow } from '@/hooks/useLocationNow';
import { useDashboardHistory } from '@/features/production-orders/hooks/useDashboardHistory';
import { useQAReportStats } from './useQAReports';
import { calcPercentDelta } from '../utils/delta';
import type { DateRange } from '../types';

const POLL_INTERVAL_MS = 60_000;

interface TVMetrics {
  itemsPrinted: number;
  totalOrders: number;
  totalDefects: number;
  defectRate: number;
  totalProcessed: number;
}

interface TVDelta {
  /** Percent change vs prior week's same weekday. Null when no prior signal. */
  percent: number | null;
  /** Absolute prior-week value (for the comparison subtitle). */
  prior: number;
}

export interface ReportsTVData {
  /** Today's running totals in the location's local calendar day. */
  today: TVMetrics;
  /** Same weekday one week ago — used as the comparison baseline. */
  lastWeek: TVMetrics;
  /** Per-metric % delta vs last weekday. */
  deltas: {
    itemsPrinted: TVDelta;
    totalOrders: TVDelta;
    totalDefects: TVDelta;
    defectRate: TVDelta;
  };
  /** "Wednesday" — the weekday name being compared against. */
  lastWeekdayLabel: string;
  /** Date the TV is showing as "today" (in the location's TZ). */
  today_date: Date;
  isLoading: boolean;
  error: Error | null;
}

const ZERO_METRICS: TVMetrics = {
  itemsPrinted: 0,
  totalOrders: 0,
  totalDefects: 0,
  defectRate: 0,
  totalProcessed: 0,
};

function deltaOrNull(current: number, prior: number): TVDelta {
  return {
    percent: prior === 0 ? null : calcPercentDelta(current, prior),
    prior,
  };
}

/**
 * Composes the four queries (today + last-weekday × stats + history) the
 * Reports TV needs. All TZ semantics are inherited from `useLocationNow` and
 * the existing tRPC pipeline — see comments in `useQAReports.ts` and
 * `useDashboardHistory.ts` for why we pass calendar dates, not instants.
 */
export function useReportsTVData(
  locationId: string | undefined,
  timezone: string | undefined,
): ReportsTVData {
  const now = useLocationNow(timezone);

  // Re-derive the ranges only when `now`'s calendar day changes. `useLocationNow`
  // ticks every 60s, so without this the range objects would churn on every
  // tick and force a refetch even when the date hasn't moved.
  const { todayRange, lastWeekRange, lastWeekdayLabel } = useMemo(() => {
    const today: DateRange = { from: startOfDay(now), to: endOfDay(now) };
    const lastWeekday = subDays(now, 7);
    const lastWeek: DateRange = {
      from: startOfDay(lastWeekday),
      to: endOfDay(lastWeekday),
    };
    return {
      todayRange: today,
      lastWeekRange: lastWeek,
      lastWeekdayLabel: format(lastWeekday, 'EEEE'),
    };
    // `format` of `now` to 'yyyy-MM-dd' captures the calendar-day identity
    // without thrashing on every minute-tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [format(now, 'yyyy-MM-dd')]);

  // Today — live, polled every 60s.
  const todayStats = useQAReportStats(locationId, todayRange, 'day', true, {
    pollIntervalMs: POLL_INTERVAL_MS,
  });
  const todayHistory = useDashboardHistory(locationId, todayRange);

  // Last weekday — historical, no polling, longer cache.
  const lastWeekStats = useQAReportStats(locationId, lastWeekRange, 'day', true, {
    isHistorical: true,
  });
  const lastWeekHistory = useDashboardHistory(locationId, lastWeekRange, {
    isHistorical: true,
  });

  const today: TVMetrics = useMemo(() => {
    const s = todayStats.data?.summaryStats;
    const printed = (todayHistory.data?.history ?? []).reduce(
      (sum, d) => sum + (d.garmentsPrinted ?? 0),
      0,
    );
    if (!s) return { ...ZERO_METRICS, itemsPrinted: printed };
    const defectRate = s.totalProcessed > 0
      ? (s.totalDefects / s.totalProcessed) * 100
      : 0;
    return {
      itemsPrinted: printed,
      totalOrders: s.totalOrders ?? 0,
      totalDefects: s.totalDefects ?? 0,
      defectRate,
      totalProcessed: s.totalProcessed ?? 0,
    };
  }, [todayStats.data, todayHistory.data]);

  const lastWeek: TVMetrics = useMemo(() => {
    const s = lastWeekStats.data?.summaryStats;
    const printed = (lastWeekHistory.data?.history ?? []).reduce(
      (sum, d) => sum + (d.garmentsPrinted ?? 0),
      0,
    );
    if (!s) return { ...ZERO_METRICS, itemsPrinted: printed };
    const defectRate = s.totalProcessed > 0
      ? (s.totalDefects / s.totalProcessed) * 100
      : 0;
    return {
      itemsPrinted: printed,
      totalOrders: s.totalOrders ?? 0,
      totalDefects: s.totalDefects ?? 0,
      defectRate,
      totalProcessed: s.totalProcessed ?? 0,
    };
  }, [lastWeekStats.data, lastWeekHistory.data]);

  const deltas = useMemo(
    () => ({
      itemsPrinted: deltaOrNull(today.itemsPrinted, lastWeek.itemsPrinted),
      totalOrders: deltaOrNull(today.totalOrders, lastWeek.totalOrders),
      totalDefects: deltaOrNull(today.totalDefects, lastWeek.totalDefects),
      defectRate: deltaOrNull(today.defectRate, lastWeek.defectRate),
    }),
    [today, lastWeek],
  );

  return {
    today,
    lastWeek,
    deltas,
    lastWeekdayLabel,
    today_date: now,
    isLoading:
      todayStats.isLoading ||
      todayHistory.isLoading ||
      lastWeekStats.isLoading ||
      lastWeekHistory.isLoading,
    error:
      (todayStats.error as Error | null) ??
      (todayHistory.error as Error | null) ??
      null,
  };
}
